import PropTypes from "prop-types";
import React, { useState } from "react";
import axios from "axios";
import ReactOnRails from "react-on-rails";

import StyledInput from "../Shared/StyledInput";

const CreateExpenseModal = ({
  visible,
  onClose,
  categories = [],
  supportedCurrencies = [],
  defaultCurrency,
  expensesPath,
  onSuccess,
  month,
  useMonth = false,
}) => {
  if (!visible) {
    return null;
  }

  const [amount, setAmount] = useState();
  const [currency, setCurrency] = useState(defaultCurrency);
  const [category_id, setCategory] = useState();
  const [comment, setComment] = useState("");
  const [error, setError] = useState(null);
  const [created_at, setCreatedAt] = useState(
    new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16)
  );
  const [loading, setLoading] = useState(false);

  const resetFields = () => {
    setAmount("");
    setCategory("");
    setComment("");
    setError(null);
  };

  const submitForm = (afterAction = "close") => {
    setLoading(true);
    axios
      .post(
        `${expensesPath}.json`,
        {
          expense: {
            amount,
            currency,
            category_id,
            comment,
            created_at,
          },
          ...(useMonth && { month }),
        },
        {
          headers: ReactOnRails.authenticityHeaders({}),
        }
      )
      .then((response) => {
        const { data, total_list_value } = response.data;
        console.log("Expense added");
        onSuccess(data, total_list_value);
        switch (afterAction) {
          case "reset":
            resetFields();
            break;
          case "close":
          default:
            onClose();
        }
      })
      .catch((error) => {
        if (error.response) {
          setError(error.response.data.errors);
        }

        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div
      id="create-expense-modal"
      tabIndex="-1"
      className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 w-full md:inset-0 h-full max-h-full bg-gray-500 bg-opacity-50"
    >
      <div className="flex justify-center items-center w-full">
        <div className="relative p-4 w-full max-w-2xl max-h-full mt-16">
          <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
            <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Add Expense
              </h3>
              <button
                onClick={onClose}
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                data-modal-hide="default-modal"
              >
                <svg
                  className="w-3 h-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
              </button>
            </div>

            <div className="p-4 md:p-5 space-y-4">
              <div>
                <label
                  htmlFor="number-input"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Enter amount:
                </label>
                <div className="relative parent-focus">
                  <StyledInput
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    type="number"
                    id="amount"
                    name="amount"
                    autoFocus={true}
                    step={0.01}
                    placeholder="0,00"
                  />
                  <select
                    className="absolute right-0 top-0 h-full bg-gray-50 border border-gray-300 text-gray-900 text-sm border border-l rounded-none rounded-r-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white child-focus"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    {supportedCurrencies.map((currency) => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label
                  htmlFor="category_id"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Select category (optional)
                </label>
                <select
                  id="category_id"
                  value={category_id}
                  onChange={(e) => setCategory(e.target.value)}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                >
                  <option key={"default"} selected>
                    Select Category
                  </option>
                  {(categories || []).map((category) => (
                    <option value={category.id} key={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="comment"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Comment (optional)
                </label>
                <StyledInput
                  type="text"
                  id="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="created_at"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Date
                </label>
                <input
                  type="datetime-local"
                  id="start"
                  value={created_at}
                  onChange={(e) => setCreatedAt(e.target.value)}
                  name="created_at"
                  min="2018-01-01"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                />
              </div>
              {error && <div className="text-red-500">{error}</div>}
            </div>

            <div className="flex items-center p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600 space-x-2">
              <button
                onClick={submitForm}
                disabled={loading}
                data-modal-hide="default-modal"
                type="button"
                className="disabled:bg-gray-500 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
              >
                Create
              </button>
              <button
                onClick={() => submitForm("reset")}
                disabled={loading}
                data-modal-hide="default-modal"
                type="button"
                className="disabled:bg-gray-500 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
              >
                Create next
              </button>
              <button
                onClick={onClose}
                data-modal-hide="default-modal"
                type="button"
                className="py-2.5 px-5 ms-3 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CreateExpenseModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  categories: PropTypes.array,
  defaultCurrency: PropTypes.string.isRequired,
  expensesPath: PropTypes.string.isRequired,
  month: PropTypes.string,
  useMonth: PropTypes.bool,
};

export default CreateExpenseModal;
